/**
 * Phase 2.1: Supportive Messaging Templates
 * 
 * Rules applied:
 * - Losing too fast: >1% bodyweight/week for 2 weeks (0.75% for leaner clients)
 * - High hunger: Week 1 = advice only, Week 2+ with good adherence = small increase
 * - No increases during: PMS, high stress, low adherence, first 2-3 weeks
 * - Tone: warm, simple, encouraging, no calorie numbers
 */

export const BUCKET_MESSAGES = {
  // ==================== TIER 1: SAFETY & DATA QUALITY ====================
  
  data_quality_issue: {
    title: "Let's Get Back on Track",
    message: "I'm missing some info from this week, so I'm keeping everything the same for now. No worries, just try to log your weight a few times this week and I'll have a clearer picture next time.",
    tone: "neutral",
    icon: "📋"
  },

  calorie_floor_reached: {
    title: "You've Hit a Safe Limit",
    message: "Your body needs a certain amount of fuel to stay healthy and energized. I've reached the lowest I'm comfortable going, so I'm holding steady here. This is completely normal, trust the process.",
    tone: "reassuring",
    icon: "🛡️"
  },

  stalled_at_floor: {
    title: "Time for a New Approach",
    message: "You've been working hard, but your body might need something different right now. I'll be in touch with a plan that works better for this stage. Hang tight, I've got you.",
    tone: "supportive",
    icon: "🔄"
  },

  // ==================== TIER 2: ADHERENCE ====================
  // Note: low_adherence has sub-variants based on weight trend and adherence level
  // The frontend determines which variant to show - backend bucket remains "low_adherence"

  // Default low_adherence message (very low adherence <50%)
  low_adherence: {
    title: "Tough Week? That's Okay.",
    message: "Tough week? That happens. No changes to your plan this week. The goal is simply to get your consistency back, that's what unlocks results.",
    tone: "compassionate",
    icon: "💪",
    showFocusTip: true
  },

  // Variant: Low adherence + weight plateau
  low_adherence_plateau: {
    title: "Consistency First",
    message: "Tough week? That happens. No changes to your plan this week. The goal is simply to get your consistency back, that's what unlocks results.",
    tone: "supportive",
    icon: "🎯",
    showFocusTip: true
  },

  // Variant: Low adherence + still losing weight
  low_adherence_losing: {
    title: "Progress Despite a Tough Week",
    message: "Tough week? That happens. No changes to your plan this week. The goal is simply to get your consistency back, that's what unlocks results.",
    tone: "positive",
    icon: "🌟",
    showFocusTip: true
  },

  // Variant: Moderate adherence (60-79%) - almost there
  low_adherence_moderate: {
    title: "Almost There!",
    message: "Tough week? That happens. No changes to your plan this week. The goal is simply to get your consistency back, that's what unlocks results.",
    tone: "encouraging",
    icon: "👏",
    showFocusTip: true
  },

  weekend_inconsistency: {
    title: "Weekends Are Tricky",
    message: "You crushed it during the week! Weekends can be harder with social events and different routines. Try picking one or two meals to stay on track this weekend, that's often enough to keep momentum going.",
    tone: "encouraging",
    icon: "🎯"
  },

  // ==================== TIER 3: COMFORT METRICS ====================

  high_hunger: {
    title: "Feeling Hungrier Lately?",
    message: "Your hunger has been higher this week. That's useful feedback. Try adding more vegetables, protein, or spreading meals out differently. No plan changes yet, let's see how next week goes.",
    tone: "caring",
    icon: "🥗"
  },

  persistent_hunger: {
    title: "Still Feeling Hungry?",
    message: "Hunger has been up for a couple weeks now, and you've been consistent. I've made a small tweak to help you feel more satisfied. Listen to your body, it knows what it needs.",
    tone: "caring",
    icon: "🍽️"
  },

  low_energy: {
    title: "Energy Running Low?",
    message: "Feeling tired is your body asking for something. Make sure you're sleeping enough and staying hydrated, sometimes that makes all the difference. I'm watching this closely.",
    tone: "caring",
    icon: "⚡"
  },

  high_stress: {
    title: "Stressful Week?",
    message: "When stress is high, I keep things steady. No plan changes right now, your body has enough to deal with. Focus on what you can control and be kind to yourself. This will pass.",
    tone: "calming",
    icon: "🌿"
  },

  // ==================== TIER 4: PROGRESS METRICS ====================

  losing_too_fast: {
    title: "Let's Slow It Down",
    message: "You're making great progress, maybe a little too fast! To keep this sustainable, I'm adding a bit more food this week.",
    tone: "positive",
    icon: "🏃"
  },

  plateau: {
    title: "The Scale Is Being Stubborn",
    message: "Weight has been steady for a bit, so I made a small adjustment to get things moving again. Nothing drastic, just a nudge.",
    tone: "reassuring",
    icon: "⏸️"
  },

  slow_loss: {
    title: "Let's Pick Up the Pace",
    message: "You're heading in the right direction, just moving a bit slower than I'd like. I've made a small tweak to help things along. Stay consistent and you'll see the difference soon.",
    tone: "encouraging",
    icon: "📈"
  },

  early_phase: {
    title: "Finding Your Baseline",
    message: "The first few weeks often show bigger swings as your body adjusts. I'm watching trends, not reacting to every change. Stay consistent and trust the process.",
    tone: "educational",
    icon: "📊"
  },

  // ==================== TIER 5: SPECIAL CASES ====================

  reverse_diet: {
    title: "Rebuilding Phase",
    message: "Right now I'm focusing on rebuilding your metabolism by gradually increasing your food. This isn't a detour, it's setting you up for stronger results later. Your body will thank you.",
    tone: "educational",
    icon: "🔧"
  },

  no_change_needed: {
    title: "Keep It Up!",
    message: "Everything looks great this week. Your consistency is paying off and you're making real progress. No changes needed, just keep doing what you're doing!",
    tone: "celebratory",
    icon: "🌟"
  },

  // Medium adherence on track - slightly less celebratory, more encouraging
  medium_adherence_on_track: {
    title: "Solid Progress!",
    message: "You're doing well and making progress. A little more consistency would make results even more predictable, but you're definitely on the right track. Keep it going!",
    tone: "encouraging",
    icon: "👍"
  },

  manual_review_required: {
    title: "Coach Is Reviewing",
    message: "Your situation needs some personal attention, so I'm taking a closer look. I'll reach out soon with a plan made just for you. You're in good hands.",
    tone: "reassuring",
    icon: "👀"
  },

  // ==================== CYCLE-AWARE (PMS/LUTEAL) ====================

  luteal_phase: {
    title: "That Time of the Month",
    message: "Water fluctuations and extra hunger are totally normal right now. No changes this week, your body is doing its thing. Be gentle with yourself and don't stress about the scale.",
    tone: "calming",
    icon: "🌸"
  }
};

// Low adherence focus tips - exactly ONE shown per week (rotates)
export const LOW_ADHERENCE_FOCUS_TIPS = [
  "Aim for one solid meal per day from the plan. One is enough.",
  "Keep weekdays simple: repeat the same meals Monday–Friday if that helps.",
  "If weekends are the issue, just focus on logging Saturday and Sunday, even if it's not perfect.",
  "Protein first: try to include a protein source at each meal.",
  "Progress doesn't require perfect. If you hit ~70% consistency, that's a win."
];

// Optional reassurance line for low adherence
export const LOW_ADHERENCE_REASSURANCE = "We don't adjust calories until consistency improves. Not as punishment, but because it works better that way.";

/**
 * Get the focus tip for low adherence based on week number
 * Uses deterministic rotation so same tip shows for same week
 * @param {number|null} weekNumber - ISO week number (1-52)
 * @param {string|null} clientId - Client ID as fallback for rotation
 * @returns {string} Single focus tip for the week
 */
export const getLowAdherenceFocusTip = (weekNumber, clientId = null) => {
  let index = 0;
  
  if (weekNumber && typeof weekNumber === 'number') {
    // Use week number for rotation
    index = weekNumber % LOW_ADHERENCE_FOCUS_TIPS.length;
  } else if (clientId && typeof clientId === 'string') {
    // Fallback: use client ID hash for stable rotation
    let hash = 0;
    for (let i = 0; i < clientId.length; i++) {
      hash = ((hash << 5) - hash) + clientId.charCodeAt(i);
      hash = hash & hash; // Convert to 32-bit integer
    }
    index = Math.abs(hash) % LOW_ADHERENCE_FOCUS_TIPS.length;
  }
  
  return LOW_ADHERENCE_FOCUS_TIPS[index];
};

/**
 * Get message for a specific bucket
 * @param {string} bucket - The bucket name
 * @returns {object} Message object with title, message, tone, and icon
 */
export const getBucketMessage = (bucket) => {
  return BUCKET_MESSAGES[bucket] || BUCKET_MESSAGES.no_change_needed;
};

/**
 * Get the appropriate low_adherence message variant based on context
 * @param {number} adherence - Overall adherence percentage (0-100)
 * @param {number|null} weightChange - Weekly weight change (negative = losing)
 * @returns {string} The specific low_adherence variant key to use
 */
export const getLowAdherenceVariant = (adherence, weightChange) => {
  // Moderate adherence (60-79%) - "Almost There!"
  if (adherence >= 60) {
    return 'low_adherence_moderate';
  }
  
  // For adherence <60%, check weight trend first
  if (weightChange !== null) {
    // Losing weight despite low adherence - "Progress Despite Tough Week"
    if (weightChange < -0.1) {
      return 'low_adherence_losing';
    }
    
    // Plateau (weight stable ±0.25kg) - "Focus on Consistency First"
    if (Math.abs(weightChange) <= 0.25) {
      return 'low_adherence_plateau';
    }
  }
  
  // Very low adherence (<50%) with weight gain or no data - default message
  if (adherence < 50) {
    return 'low_adherence';
  }
  
  // Default for 50-59% adherence with weight gain
  return 'low_adherence_plateau';
};

/**
 * Get a simple one-liner for dashboard tiles
 * @param {string} bucket - The bucket name
 * @returns {string} Short summary message
 */
export const getBucketSummary = (bucket) => {
  const summaries = {
    data_quality_issue: "We need more data, no changes this week.",
    calorie_floor_reached: "Holding steady at a safe level.",
    stalled_at_floor: "Your coach is planning a new approach.",
    low_adherence: "Focus on consistency first, no changes yet.",
    low_adherence_plateau: "Consistency is the focus right now.",
    low_adherence_losing: "Still progressing, tighten up for predictable results.",
    low_adherence_moderate: "You're close, a bit more consistency unlocks progress.",
    weekend_inconsistency: "Great weekdays! Let's work on weekends.",
    high_hunger: "Noted your hunger, let's see how next week goes.",
    persistent_hunger: "Small tweak to help with ongoing hunger.",
    low_energy: "Watching your energy levels closely.",
    high_stress: "Keeping things steady during stress.",
    losing_too_fast: "Adding fuel for sustainable progress.",
    plateau: "Small tweak to break through the plateau.",
    slow_loss: "Small adjustment to pick up the pace.",
    early_phase: "Watching trends as your body adjusts.",
    reverse_diet: "Building up your metabolism.",
    no_change_needed: "On track, keep it up!",
    medium_adherence_on_track: "Good progress, a bit more consistency will help.",
    manual_review_required: "Your coach is reviewing personally.",
    luteal_phase: "No changes during this phase, totally normal."
  };
  
  return summaries[bucket] || "Your plan is set for the week.";
};

/**
 * Get tone-based styling class
 * @param {string} tone - The message tone
 * @returns {string} Tailwind CSS classes for the tone
 */
export const getToneStyles = (tone) => {
  const styles = {
    neutral: "bg-gray-50 border-gray-200 text-gray-700",
    reassuring: "bg-blue-50 border-blue-200 text-blue-700",
    supportive: "bg-purple-50 border-purple-200 text-purple-700",
    compassionate: "bg-amber-50 border-amber-200 text-amber-700",
    encouraging: "bg-green-50 border-green-200 text-green-700",
    caring: "bg-pink-50 border-pink-200 text-pink-700",
    calming: "bg-teal-50 border-teal-200 text-teal-700",
    positive: "bg-emerald-50 border-emerald-200 text-emerald-700",
    educational: "bg-indigo-50 border-indigo-200 text-indigo-700",
    celebratory: "bg-yellow-50 border-yellow-200 text-yellow-700"
  };
  
  return styles[tone] || styles.neutral;
};

export default BUCKET_MESSAGES;
